import { isAnyOf } from '@reduxjs/toolkit';

import ooeConstants from '../constants';
import { AppStartListening } from '../listenerMiddleware';
import { actions as orderActions } from '../order/reducers';
import { State } from '../reducers';
import { logEvent } from '../services/analytics';
import { actions as userActions } from '../user/reducer';
import { selectBypassBusinessRules } from '../user/selectors';
import { addressErrorMessages } from '../util/customerErrorMessages';
import { actions as eventDetailsActions } from './reducer';
import {
  selectAddressSearchResults,
  selectAddressSearchSelectedPlaceId,
  selectAddressSearchValue,
} from './selectors';
import { autocompleteAddress, validateAddress } from './thunks';
import { getAddressSearchSeed } from './utils';

const addEventDetailsListeners = (startListening: AppStartListening) => {
  // Persists across effect runs (unlike cancelActiveListeners, which only reaches a
  // pending debounce timer, not a request already dispatched by a previous run) so a
  // superseded autocomplete or validate can have its Connect call cancelled outright.
  let activeAddressSearch: { abort: (reason?: string) => void } | undefined;
  let activeAddressValidation: { abort: (reason?: string) => void } | undefined;

  const abortInFlightAddressRequests = (reason: string) => {
    activeAddressSearch?.abort(reason);
    activeAddressSearch = undefined;
    activeAddressValidation?.abort(reason);
    activeAddressValidation = undefined;
  };

  startListening({
    actionCreator: orderActions.setDetailsTime,
    effect: (action) => {
      const { type } = action;
      logEvent(type);
    },
  });

  startListening({
    actionCreator: orderActions.setDetailsDate,
    effect: (action) => {
      const { type, payload } = action;
      logEvent(type, payload ?? undefined);
    },
  });

  startListening({
    actionCreator: orderActions.setDetailsDate,
    effect: (_, { getState, dispatch }) => {
      const state = getState() as State;
      const bypassBusinessRules = selectBypassBusinessRules(state);
      if (!bypassBusinessRules) {
        dispatch(orderActions.setDetailsTime(''));
      }
    },
  });

  startListening({
    matcher: isAnyOf(orderActions.changeDestination, userActions.updateUserLocation),
    effect: (_, { getState, dispatch }) => {
      const state = getState() as State;
      const bypassBusinessRules = selectBypassBusinessRules(state);

      if (bypassBusinessRules) {
        return;
      }

      dispatch(orderActions.setDetailsTime(''));
      dispatch(orderActions.setDetailsDate(null));
    },
  });

  startListening({
    actionCreator: eventDetailsActions.setAddressSearchValue,
    effect: async (action, listenerApi) => {
      const { cancelActiveListeners, delay, dispatch, extra, getState } = listenerApi;

      if (action.payload.autocomplete === false) {
        return;
      }

      // debounce (https://redux-toolkit.js.org/api/createListenerMiddleware#complex-async-workflows)
      // cancelActiveListeners only reaches other runs of the same listener entry.
      cancelActiveListeners();

      const state = getState() as State;
      const query = selectAddressSearchValue(state).trim();

      if (query.length < ooeConstants.ADDRESS_SEARCH_MIN_TYPEAHEAD_LENGTH) {
        abortInFlightAddressRequests('Superseded by a newer address search');
        return;
      }

      await delay(ooeConstants.ADDRESS_SEARCH_DEBOUNCE_TIME);

      if (extra.testUtils?.debounceCallback) {
        extra.testUtils.debounceCallback(action);
      }

      abortInFlightAddressRequests('Superseded by a newer address search');
      activeAddressSearch = dispatch(autocompleteAddress());
    },
  });

  startListening({
    actionCreator: eventDetailsActions.selectAddressSearchResult,
    effect: (action, { dispatch }) => {
      activeAddressValidation?.abort('Superseded by a newer address validation');
      activeAddressValidation = dispatch(validateAddress(action.payload));
    },
  });

  startListening({
    actionCreator: orderActions.clearAddressFields,
    effect: (_, { dispatch }) => {
      dispatch(eventDetailsActions.clearAddressSearch());
    },
  });

  startListening({
    actionCreator: eventDetailsActions.blurAddressSearch,
    effect: (_, { dispatch, getState }) => {
      const state = getState() as State;
      const query = selectAddressSearchValue(state).trim();
      const results = selectAddressSearchResults(state);
      const selectedPlaceId = selectAddressSearchSelectedPlaceId(state);

      if (!selectedPlaceId && (!query || results.length === 0)) {
        dispatch(
          eventDetailsActions.setAddressValidationError(addressErrorMessages.ERR_NOT_DELIVERABLE.message),
        );
      }
    },
  });

  startListening({
    actionCreator: orderActions.selectPastDeliveryAddress,
    effect: (action, listenerApi) => {
      const { dispatch } = listenerApi;
      const address = action.payload;
      const { mainText, secondaryText, searchValue } = getAddressSearchSeed(address);

      if (address.placeId) {
        dispatch(
          eventDetailsActions.selectAddressSearchResult({
            placeId: address.placeId,
            structuredFormatting: { mainText, secondaryText },
          }),
        );
      } else {
        dispatch(eventDetailsActions.seedAddressSearchFromEdit({ value: searchValue, placeId: null }));
      }
    },
  });
};

export default addEventDetailsListeners;
